/**
 * Jira Context Utilities
 *
 * Utilities for accessing Jira Cloud context when running as a Connect app.
 * Uses the Atlassian Connect JavaScript library (AP) to get context data.
 */

/**
 * Check if the app is running in a Jira Connect iframe
 * @returns {boolean} True if AP object is available (running in Jira)
 */
export const isJiraConnect = () => {
  return typeof window !== 'undefined' && typeof window.AP !== 'undefined'
}

// ============================================================================
// JWT TOKEN MANAGEMENT
// ============================================================================

/**
 * Token cache to avoid unnecessary AP.context.getToken() calls
 * JWT tokens from Jira Connect are valid for ~5 minutes
 */
let cachedToken = null
let tokenExpiresAt = null
const TOKEN_REFRESH_BUFFER_MS = 30000 // Refresh 30s before expiry

/**
 * Get JWT token for API authentication
 *
 * In Jira Connect mode: Fetches token from AP.context.getToken()
 * In standalone/dev mode: Uses VITE_DEV_JWT_TOKEN environment variable
 *
 * Implements caching to avoid excessive AP calls (tokens valid ~5 mins)
 *
 * @returns {Promise<string|null>} JWT token or null if unavailable
 */
export const getJwtToken = async () => {
  // Check cache first (only in Jira Connect mode)
  if (isJiraConnect() && cachedToken && tokenExpiresAt) {
    const now = Date.now()
    if (now < tokenExpiresAt - TOKEN_REFRESH_BUFFER_MS) {
      return cachedToken
    }
    // Token expired or about to expire, clear cache
    cachedToken = null
    tokenExpiresAt = null
  }

  // Running in Jira Connect iframe - use AP.context.getToken()
  if (isJiraConnect()) {
    return new Promise((resolve) => {
      window.AP.context.getToken((token) => {
        if (token) {
          // Cache the token - Jira tokens are typically valid for ~5 minutes
          cachedToken = token
          // Set expiry to 4.5 minutes (270 seconds) to be safe
          tokenExpiresAt = Date.now() + (270 * 1000)
          resolve(token)
        } else {
          console.warn('AP.context.getToken returned empty token')
          resolve(null)
        }
      })
    })
  }

  // Running in standalone/dev mode - use environment variable
  const devToken = import.meta.env.VITE_DEV_JWT_TOKEN
  if (devToken) {
    return devToken
  }

  console.warn('No JWT token available: Not in Jira Connect and VITE_DEV_JWT_TOKEN not set')
  return null
}

/**
 * Clear the cached JWT token
 * Useful when token is rejected (401) to force refresh on next request
 */
export const clearJwtTokenCache = () => {
  cachedToken = null
  tokenExpiresAt = null
}

/**
 * Get the full Jira context object
 * Returns project info, user info, and other context data
 * 
 * @returns {Promise<Object>} Context object with jira, confluence, user data
 * @throws {Error} If not running in Jira Connect environment
 */
export const getJiraContext = () => {
  return new Promise((resolve, reject) => {
    if (!isJiraConnect()) {
      reject(new Error('Not running in Jira Connect environment'))
      return
    }

    window.AP.context.getContext((context) => {
      console.log('Jira Context:', context)
      resolve(context)
    })
  })
}

/**
 * Get the current Jira project ID
 * @returns {Promise<string|null>} Project ID or null if not available
 */
export const getProjectId = async () => {
  try {
    const context = await getJiraContext()
    // Context structure: context.jira.project.id
    return context?.jira?.project?.id || null
  } catch (error) {
    console.warn('Failed to get project ID from Jira context:', error)
    return null
  }
}

/**
 * Get the current Jira project key
 * @returns {Promise<string|null>} Project key or null if not available
 */
export const getProjectKey = async () => {
  try {
    const context = await getJiraContext()
    // Context structure: context.jira.project.key
    return context?.jira?.project?.key || null
  } catch (error) {
    console.warn('Failed to get project key from Jira context:', error)
    return null
  }
}

/**
 * Get the current user information
 * @returns {Promise<Object|null>} User object with accountId, name, etc.
 */
export const getUserInfo = async () => {
  try {
    const context = await getJiraContext()
    return {
      accountId: context?.jira?.user?.accountId || null,
      userKey: context?.jira?.user?.key || null,
      userName: context?.jira?.user?.displayName || null
    }
  } catch (error) {
    console.warn('Failed to get user info from Jira context:', error)
    return null
  }
}

/**
 * Get the Atlassian host URL
 * @returns {Promise<string|null>} Host URL (e.g., "yoursite.atlassian.net")
 */
export const getHostUrl = async () => {
  try {
    const context = await getJiraContext()
    return context?.jira?.host || null
  } catch (error) {
    console.warn('Failed to get host URL from Jira context:', error)
    return null
  }
}

/**
 * Resize the iframe to fit content
 * Useful when content height changes dynamically
 */
export const resizeIframe = () => {
  if (isJiraConnect()) {
    window.AP.resize()
  }
}

/**
 * Get all context data needed for the app
 * Convenience function that fetches all relevant context at once
 * 
 * @returns {Promise<Object>} Object with projectId, projectKey, user, hostUrl
 */
export const getAllContext = async () => {
  try {
    const context = await getJiraContext()
    return {
      projectId: context?.jira?.project?.id || null,
      projectKey: context?.jira?.project?.key || null,
      user: {
        accountId: context?.jira?.user?.accountId || null,
        userKey: context?.jira?.user?.key || null,
        userName: context?.jira?.user?.displayName || null
      },
      hostUrl: context?.jira?.host || null,
      raw: context
    }
  } catch (error) {
    console.warn('Failed to get Jira context:', error)
    return {
      projectId: null,
      projectKey: null,
      user: null,
      hostUrl: null,
      raw: null
    }
  }
}

/**
 * Search for Jira users
 * Uses the Jira REST API to search for users by query string
 *
 * @param {string} query - Search query (name, email, etc.)
 * @returns {Promise<Array>} Array of user objects with accountId, displayName, avatarUrl
 */
export const searchJiraUsers = async (query = '') => {
  // Return empty array if not running in Jira Connect environment
  if (!isJiraConnect()) {
    console.warn('searchJiraUsers: Not running in Jira Connect environment')
    return []
  }

  return new Promise((resolve) => {
    window.AP.request({
      url: `/rest/api/3/user/search?query=${encodeURIComponent(query)}&maxResults=50`,
      type: 'GET',
      success: (response) => {
        try {
          const users = JSON.parse(response)
          // Filter to only include real people (accountType === 'atlassian')
          // Exclude app users like Slack, Trello, Atlas integrations (accountType === 'app')
          const filteredUsers = users.filter(user => user.accountType === 'atlassian')
          // Transform to a consistent format for the UI
          const transformedUsers = filteredUsers.map(user => ({
            accountId: user.accountId,
            displayName: user.displayName,
            avatarUrl: user.avatarUrls?.['24x24'] || user.avatarUrls?.['48x48'] || null
          }))
          resolve(transformedUsers)
        } catch (parseError) {
          console.error('Failed to parse user search response:', parseError)
          resolve([])
        }
      },
      error: (error) => {
        console.error('Failed to search Jira users:', error)
        resolve([])
      }
    })
  })
}

/**
 * Get multiple Jira users by account IDs in a single bulk API call
 * Uses the /rest/api/3/user/bulk endpoint for efficiency.
 * Processes accounts in batches of 35 to stay within URL length limits.
 *
 * @param {string[]} accountIds - Array of Jira account IDs to fetch
 * @returns {Promise<Object>} Map of accountId -> user details { accountId, displayName, avatarUrl }
 */
export const getJiraUsersBulk = async (accountIds) => {
  // Return empty map if not running in Jira Connect environment or no IDs provided
  if (!isJiraConnect() || !accountIds?.length) {
    return {}
  }

  // Batch size of 35 to stay within URL length limits (recommended by Jira)
  const BATCH_SIZE = 35
  const results = {}

  // Remove duplicates and filter out null/undefined values
  const uniqueIds = [...new Set(accountIds.filter(id => id))]

  // Process in batches of 35
  for (let i = 0; i < uniqueIds.length; i += BATCH_SIZE) {
    const batch = uniqueIds.slice(i, i + BATCH_SIZE)
    // Build query params: accountId=x&accountId=y&accountId=z
    const queryParams = batch.map(id => `accountId=${encodeURIComponent(id)}`).join('&')

    try {
      // Call bulk API
      const response = await new Promise((resolve, reject) => {
        window.AP.request({
          url: `/rest/api/3/user/bulk?${queryParams}`,
          type: 'GET',
          success: (response) => {
            try {
              const data = JSON.parse(response)
              resolve(data)
            } catch (parseError) {
              console.error('Failed to parse bulk user response:', parseError)
              reject(parseError)
            }
          },
          error: (error) => {
            console.error('Bulk user fetch failed:', error)
            reject(error)
          }
        })
      })

      // The bulk API returns { values: [...users...] }
      const users = response.values || response || []

      // Map results by accountId
      users.forEach(user => {
        if (user?.accountId) {
          results[user.accountId] = {
            accountId: user.accountId,
            displayName: user.displayName,
            avatarUrl: user.avatarUrls?.['24x24'] || user.avatarUrls?.['48x48'] || null
          }
        }
      })
    } catch (error) {
      // Log error but continue with other batches
      console.error(`Bulk user fetch failed for batch starting at index ${i}:`, error)
    }
  }

  return results
}

/**
 * Get a single Jira user by account ID
 * Useful for fetching user details when we only have the account ID
 *
 * @param {string} accountId - The Jira account ID
 * @returns {Promise<Object|null>} User object or null if not found
 */
export const getJiraUserById = async (accountId) => {
  // Return null if not running in Jira Connect environment or no accountId provided
  if (!isJiraConnect() || !accountId) {
    return null
  }

  return new Promise((resolve) => {
    window.AP.request({
      url: `/rest/api/3/user?accountId=${encodeURIComponent(accountId)}`,
      type: 'GET',
      success: (response) => {
        try {
          const user = JSON.parse(response)
          resolve({
            accountId: user.accountId,
            displayName: user.displayName,
            avatarUrl: user.avatarUrls?.['24x24'] || user.avatarUrls?.['48x48'] || null
          })
        } catch (parseError) {
          console.error('Failed to parse user response:', parseError)
          resolve(null)
        }
      },
      error: (error) => {
        console.error('Failed to get Jira user:', error)
        resolve(null)
      }
    })
  })
}

/**
 * Fetch all Jira projects the current user has access to
 * Uses the Jira REST API to get projects
 *
 * @returns {Promise<Array>} Array of project objects with id, key, name
 */
export const fetchJiraProjects = async () => {
  // Return empty array if not running in Jira Connect environment
  if (!isJiraConnect()) {
    console.warn('fetchJiraProjects: Not running in Jira Connect environment')
    return []
  }

  return new Promise((resolve) => {
    window.AP.request({
      url: '/rest/api/3/project/search?maxResults=100&orderBy=name',
      type: 'GET',
      success: (response) => {
        try {
          const data = JSON.parse(response)
          // The response has a 'values' array containing projects
          const projects = data.values || data || []
          // Transform to a consistent format for the UI
          const transformedProjects = projects.map(project => ({
            id: project.id,
            key: project.key,
            name: project.name,
            avatarUrl: project.avatarUrls?.['24x24'] || project.avatarUrls?.['48x48'] || null
          }))
          resolve(transformedProjects)
        } catch (parseError) {
          console.error('Failed to parse projects response:', parseError)
          resolve([])
        }
      },
      error: (error) => {
        console.error('Failed to fetch Jira projects:', error)
        resolve([])
      }
    })
  })
}

// ============================================================================
// JIRA ISSUE SEARCH
// ============================================================================

/**
 * Search for Jira issues
 * Uses the Jira REST API issue picker endpoint for autocomplete-style search.
 * Returns issues matching the query string (searches key and summary).
 *
 * @param {string} query - Search query (issue key or text)
 * @returns {Promise<Array>} Array of issue objects with key, summary, issueTypeIcon
 */
export const searchJiraIssues = async (query = '') => {
  // Return empty array if not running in Jira Connect environment or empty query
  if (!isJiraConnect()) {
    console.warn('searchJiraIssues: Not running in Jira Connect environment')
    return []
  }

  if (!query || query.trim().length < 2) {
    return []
  }

  return new Promise((resolve) => {
    window.AP.request({
      url: `/rest/api/3/issue/picker?query=${encodeURIComponent(query)}&currentJQL=&showSubTasks=true`,
      type: 'GET',
      success: (response) => {
        try {
          const data = JSON.parse(response)
          // Issue picker returns sections (History, Current Search, etc.)
          // Flatten all sections and get unique issues
          const allIssues = data.sections?.flatMap(section => section.issues || []) || []

          // Remove duplicates by key
          const uniqueIssues = allIssues.reduce((acc, issue) => {
            if (!acc.find(i => i.key === issue.key)) {
              acc.push(issue)
            }
            return acc
          }, [])

          // Transform to a consistent format for the UI
          // Note: Issue picker API only provides img URL, not issue type name
          const transformedIssues = uniqueIssues.map(issue => ({
            key: issue.key,
            summary: issue.summaryText || issue.summary || '',
            issueTypeIcon: issue.img || null
          }))
          resolve(transformedIssues)
        } catch (parseError) {
          console.error('Failed to parse issue search response:', parseError)
          resolve([])
        }
      },
      error: (error) => {
        console.error('Failed to search Jira issues:', error)
        resolve([])
      }
    })
  })
}

// ============================================================================
// JIRA LABELS
// ============================================================================

/**
 * Fetch all labels from the Jira instance
 * Uses the Jira REST API to get all available labels with pagination.
 * Labels in Jira are global (not project-scoped).
 * Fetches all pages (1000 per page) until isLast is true.
 *
 * @returns {Promise<Array<string>>} Array of label name strings, sorted alphabetically
 */
export const fetchJiraLabels = async () => {
  // Return empty array if not running in Jira Connect environment
  if (!isJiraConnect()) {
    console.warn('fetchJiraLabels: Not running in Jira Connect environment')
    return []
  }

  const allLabels = []
  let startAt = 0
  const maxResults = 1000
  let isLast = false

  // Fetch all pages of labels
  while (!isLast) {
    const pageLabels = await new Promise((resolve) => {
      window.AP.request({
        url: `/rest/api/3/label?startAt=${startAt}&maxResults=${maxResults}`,
        type: 'GET',
        success: (response) => {
          try {
            const data = JSON.parse(response)
            // The /rest/api/3/label endpoint returns { values: ["label1", "label2", ...], total, isLast, ... }
            isLast = data.isLast !== false // Default to true if not present
            resolve(data.values || [])
          } catch (parseError) {
            console.error('Failed to parse labels response:', parseError)
            isLast = true
            resolve([])
          }
        },
        error: (error) => {
          console.error('Failed to fetch Jira labels:', error)
          isLast = true
          resolve([])
        }
      })
    })

    allLabels.push(...pageLabels)
    startAt += maxResults
  }

  // Sort alphabetically and return
  return allLabels.sort((a, b) => a.localeCompare(b))
}

// ============================================================================
// JIRA ISSUE DETAILS
// ============================================================================

/**
 * Get Jira issue details by key
 * Fetches a single issue with summary, status, priority, type, and assignee.
 * Used to enrich bug data after linking since backend only stores the issue key.
 *
 * @param {string} issueKey - Jira issue key (e.g., "PROJ-123")
 * @returns {Promise<Object|null>} Issue details or null if fetch failed
 */
export const getJiraIssueByKey = async (issueKey) => {
  // Return null if not running in Jira Connect environment or no key provided
  if (!isJiraConnect() || !issueKey) {
    return null
  }

  return new Promise((resolve) => {
    window.AP.request({
      url: `/rest/api/3/issue/${encodeURIComponent(issueKey)}?fields=summary,status,priority,issuetype,assignee`,
      type: 'GET',
      success: (response) => {
        try {
          const issue = JSON.parse(response)
          resolve({
            key: issue.key,
            summary: issue.fields?.summary || '',
            status: issue.fields?.status?.name || null,
            priority: issue.fields?.priority?.name || null,
            issueType: issue.fields?.issuetype?.name || null,
            issueTypeIcon: issue.fields?.issuetype?.iconUrl || null,
            assigneeDisplayName: issue.fields?.assignee?.displayName || null,
            assigneeAccountId: issue.fields?.assignee?.accountId || null
          })
        } catch (parseError) {
          console.error('Failed to parse issue response:', parseError)
          resolve(null)
        }
      },
      error: (error) => {
        console.error('Failed to fetch Jira issue:', issueKey, error)
        resolve(null)
      }
    })
  })
}

// ============================================================================
// CREATE ISSUE DIALOG
// ============================================================================

/**
 * Open Jira's native create issue dialog
 * Uses AP.jira.openCreateIssueDialog() to open Jira's standard create dialog.
 * The dialog can be pre-filled with project, issue type, and field values.
 * After the user creates an issue, the callback returns the created issue(s).
 *
 * @param {Object} options - Options for the dialog
 * @param {string|number} options.projectId - Project ID to pre-select
 * @param {string|number} options.issueTypeId - Issue type ID to pre-select (e.g., Bug)
 * @param {string} options.summary - Pre-fill summary field
 * @param {string} options.description - Pre-fill description field
 * @returns {Promise<Object|null>} Created issue object or null if dialog was closed without creating
 */
export const openCreateIssueDialog = (options = {}) => {
  return new Promise((resolve, reject) => {
    // Return error if not running in Jira Connect environment
    if (!isJiraConnect()) {
      reject(new Error('Not in Jira Connect environment'))
      return
    }

    // Check if AP.jira is available
    if (!window.AP?.jira?.openCreateIssueDialog) {
      reject(new Error('AP.jira.openCreateIssueDialog is not available'))
      return
    }

    // Build dialog options
    const dialogOptions = {}

    // Add project ID if provided
    if (options.projectId) {
      dialogOptions.pid = options.projectId
    }

    // Add issue type ID if provided
    if (options.issueTypeId) {
      dialogOptions.issueType = options.issueTypeId
    }

    // Add pre-filled fields if provided
    const fields = {}
    if (options.summary) {
      fields.summary = options.summary
    }
    if (options.description) {
      fields.description = options.description
    }
    if (Object.keys(fields).length > 0) {
      dialogOptions.fields = fields
    }

    // Open the create issue dialog
    window.AP.jira.openCreateIssueDialog(
      (issues) => {
        // Callback is invoked when dialog closes
        if (issues && issues.length > 0) {
          // Return the first created issue
          resolve(issues[0])
        } else {
          // Dialog was closed without creating an issue
          resolve(null)
        }
      },
      dialogOptions
    )
  })
}

// ============================================================================
// CROSS-MODULE NAVIGATION
// ============================================================================

/**
 * Navigate to an add-on module (general page) from a different iframe
 * Uses AP.navigator.go() to switch from issue panel/dialog to the main app.
 * Before navigating, saves the target route to localStorage so the
 * RouteSynchronizer in main.jsx can restore it on load.
 *
 * @param {string} moduleKey - The module key from atlassian-connect.json (e.g., 'test-management-page')
 * @param {string} targetRoute - The route to navigate to after module loads (e.g., '/releases/5/scenario/10')
 */
export const navigateToAddonModule = (moduleKey, targetRoute) => {
  // Save the target route to localStorage so RouteSynchronizer picks it up
  if (targetRoute) {
    localStorage.setItem('current-route', targetRoute)
  }

  if (isJiraConnect() && window.AP?.navigator?.go) {
    window.AP.navigator.go('addonModule', { moduleKey })
  } else {
    // Dev mode fallback — open the route in a new tab
    console.warn('AP.navigator not available — using fallback window.open')
    window.open(`/#${targetRoute}`, '_blank')
  }
}

// Addon key from atlassian-connect.json — used to build absolute Connect URLs.
const ADDON_KEY = 'com.zen.tm'

/**
 * Open an add-on module in a new browser tab.
 *
 * AP.navigator.go() only navigates the current parent tab — it has no
 * "open in new tab" option. To open a module in a fresh tab we build the
 * absolute Connect URL and pass it to window.open().
 *
 * The route is appended as a `#!{route}` hash fragment, which is the
 * format AP.history reads on load. RouteSynchronizer in main.jsx calls
 * AP.history.getState() and navigates the React router to that route,
 * so the new tab lands directly on the scenario detail page.
 *
 * @param {string} moduleKey - The module key from atlassian-connect.json
 * @param {string} targetRoute - The route to land on (e.g. '/scenario/10')
 */
export const openAddonModuleInNewTab = (moduleKey, targetRoute) => {
  // Jira Connect injects xdm_e as the host base URL (e.g. https://acme.atlassian.net)
  const host = new URLSearchParams(window.location.search).get('xdm_e')
  const hash = targetRoute ? `#!${targetRoute}` : ''

  if (host) {
    const url = `${host}/plugins/servlet/ac/${ADDON_KEY}/${moduleKey}${hash}`
    window.open(url, '_blank', 'noopener,noreferrer')
  } else {
    // Dev mode fallback — open the route at the current origin
    console.warn('xdm_e not available — using dev fallback window.open')
    window.open(`/#${targetRoute}`, '_blank', 'noopener,noreferrer')
  }
}

// ============================================================================
// NAVIGATION HISTORY MANAGEMENT (for shareable URLs)
// ============================================================================

/**
 * Push navigation state to Jira's URL (adds to browser history)
 * Pass route as STRING directly - Jira will use it as URL hash fragment
 * This creates shareable URLs like: .../page#!/releases/123
 *
 * @param {string} route - The route path (e.g., '/releases/123')
 */
export const pushNavigationState = (route) => {
  if (isJiraConnect() && window.AP?.history?.pushState) {
    // Pass string directly - becomes hash fragment in parent URL
    window.AP.history.pushState(route)
  }
}

/**
 * Replace current navigation state in Jira's URL
 * Pass route as STRING directly - Jira will use it as URL hash fragment
 * Use this instead of pushState to avoid adding to browser history
 *
 * @param {string} route - The route path
 */
export const replaceNavigationState = (route) => {
  if (isJiraConnect() && window.AP?.history?.replaceState) {
    // Pass string directly - becomes hash fragment in parent URL
    window.AP.history.replaceState(route)
  }
}

/**
 * Get the saved navigation state from Jira's URL
 * Returns the route string from the URL hash fragment
 *
 * @returns {Promise<string|null>} The saved route or null
 */
export const getNavigationState = () => {
  return new Promise((resolve) => {
    if (isJiraConnect() && window.AP?.history?.getState) {
      window.AP.history.getState((state) => {
        // State could be a string (route directly) or an object (legacy)
        if (typeof state === 'string') {
          resolve(state || null)
        } else if (state?.route) {
          // Handle legacy object format
          resolve(state.route)
        } else {
          resolve(null)
        }
      })
    } else {
      resolve(null)
    }
  })
}

